import {
  VIBE_BUILD_JOB_STATUS_READ_VALUES,
  VIBE_DEPLOYMENT_STATUS_READ_VALUES
} from "../../../api/domains/vibe/schemas/status-read.schemas";
import { DbEnum } from "../../../generated/prisma-enums";
import { enumReadSchema } from "../../../shared/core/enum-read.schema";
import { VIBE_COST_SAFETY_ACTIVE_STATUSES } from "../../../shared/domain/vibe/cost-safety-active-statuses";
import { VIBE_GIT_PUSH_NO_DEPLOY_REASONS } from "../../../shared/domain/vibe/git-push-no-deploy-reasons";
import { VIBE_USAGE_TYPES } from "../../../shared/domain/vibe/usage-types";

/**
 * The enum fields of the audit payloads as the feed's READERS parse them: each
 * listed value as itself, and a value a newer backend wrote that this contract
 * does not list as that field's unlisted value, never a failed feed page.
 *
 * Every one of these is a PAST fact on an append-only row — the status a
 * deployment left, the slot it held, the reason a grant moved — so none of them
 * is a value a reader decides on, and each has an honest rendering when it is
 * unlisted: the server's own word. That is the condition `enumReadSchema` sets,
 * and why the deployment and build-job statuses read leniently HERE while the
 * live statuses on a deployment read strictly in `status-read.schemas.ts`.
 *
 * The two statuses are read over those READER lists rather than the database
 * tuples, so the listed half is exactly what readers have words for; the
 * reader list leading the database is what keeps every value the backend can
 * write inside it. Every other field reads over the same list it is emitted
 * from.
 *
 * The emitting branches never use these: a backend writing a value it does not
 * list is a bug, and the strict enum is what reports it.
 */

export const VibeAuditDeploymentStatusReadSchema = enumReadSchema(
  VIBE_DEPLOYMENT_STATUS_READ_VALUES
);

export const VibeAuditBuildJobStatusReadSchema = enumReadSchema(VIBE_BUILD_JOB_STATUS_READ_VALUES);

/** `DEPLOYMENT_BUILD_SUCCEEDED.nextStatus` — the two statuses a build success advances to. */
export const VibeAuditBuildSucceededNextStatusReadSchema = enumReadSchema([
  "AWAITING_APPROVAL",
  "DEPLOYING"
] as const);

export const VibeAuditCostSafetyActiveStatusReadSchema = enumReadSchema(
  VIBE_COST_SAFETY_ACTIVE_STATUSES
);

export const VibeAuditOrgCostSafetyStatusReadSchema = enumReadSchema(
  DbEnum.VIBE_ORG_COST_SAFETY_STATUS_VALUES
);

export const VibeAuditUsageTypeReadSchema = enumReadSchema(VIBE_USAGE_TYPES);

export const VibeAuditCapacitySubjectReadSchema = enumReadSchema(
  DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES
);

export const VibeAuditEdgeReachabilityReadSchema = enumReadSchema(
  DbEnum.VIBE_APP_EDGE_REACHABILITY_VALUES
);

export const VibeAuditEnvVarScopeReadSchema = enumReadSchema(DbEnum.VIBE_ENV_VAR_SCOPE_VALUES);

export const VibeAuditCardGrantStatusReadSchema = enumReadSchema(
  DbEnum.VIBE_APP_CARD_GRANT_STATUS_VALUES
);

export const VibeAuditGrantTransitionReasonReadSchema = enumReadSchema(
  DbEnum.GRANT_TRANSITION_REASON_VALUES
);

export const VibeAuditCardProjectionReadSchema = enumReadSchema(DbEnum.VIBE_CARD_PROJECTION_VALUES);

export const VibeAuditGitPushNoDeployReasonReadSchema = enumReadSchema(
  VIBE_GIT_PUSH_NO_DEPLOY_REASONS
);
